// Task detail modal — controls block (design handoff "Direction C", inline chips).
// Two controlled pieces: TaskHeaderPills (editable Priority + Status pills) and
// TaskChipRow (Assignee / Client / Due chips). Built on the app's existing
// primitives + shared task color maps. Owns no task state — each change calls back.
import { useState } from "react";
import { ChevronDown, User, Shield, Calendar as CalendarIcon, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Calendar } from "@/components/ui/calendar";
import { cn, parseDateLocal } from "@/lib/utils";
import { format } from "date-fns";
import {
  priorityColors,
  statusColors,
  priorityDot,
  statusDot,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  formatTaskValueLabel,
} from "@/lib/taskColors";

type Person = { id: string; firstName?: string; lastName?: string; role?: string };

const personName = (p?: Person) =>
  p ? `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() : "";

const initialsOf = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

// Deterministic gradient avatar (matches the handoff's look without a stored image).
const GRADIENTS = [
  ["#94A3B8", "#475569"],
  ["#6EE7B7", "#059669"],
  ["#C4B5FD", "#7C3AED"],
  ["#FDBA74", "#EA580C"],
  ["#7DD3FC", "#2563EB"],
  ["#F9A8D4", "#DB2777"],
];
function InitialsAvatar({ name, size = 26 }: { name: string; size?: number }) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const [a, b] = GRADIENTS[Math.abs(hash) % GRADIENTS.length];
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
        background: `linear-gradient(135deg, ${a}, ${b})`,
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

// ─────────────────── Header pills ───────────────────

function Pill({
  value,
  filled,
  onChange,
  options,
  dots,
  ariaLabel,
}: {
  value: string;
  filled: boolean;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  dots: Record<string, string>;
  ariaLabel: string;
}) {
  const colorClass = filled ? priorityColors[value] : statusColors[value];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={ariaLabel}
          aria-haspopup="menu"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border border-transparent px-2.5 py-1 text-xs font-bold uppercase tracking-wide transition hover:brightness-95 hover:shadow-sm",
            colorClass,
          )}
        >
          {formatTaskValueLabel(value)}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[200px]">
        {options.map((o) => (
          <DropdownMenuItem
            key={o.value}
            onSelect={() => onChange(o.value)}
            className="gap-2.5"
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: dots[o.value] }}
            />
            <span className={cn("flex-1", o.value === value && "font-semibold")}>
              {o.label}
            </span>
            {o.value === value && <Check className="h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TaskHeaderPills({
  status,
  priority,
  onStatusChange,
  onPriorityChange,
}: {
  status: string;
  priority: string;
  onStatusChange: (v: string) => void;
  onPriorityChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Pill
        value={priority}
        filled
        onChange={onPriorityChange}
        options={PRIORITY_OPTIONS}
        dots={priorityDot}
        ariaLabel="Change priority"
      />
      <Pill
        value={status}
        filled={false}
        onChange={onStatusChange}
        options={STATUS_OPTIONS}
        dots={statusDot}
        ariaLabel="Change status"
      />
    </div>
  );
}

// ─────────────────── Inline chip row ───────────────────

const chipBase =
  "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12.5px] font-medium";
const chipEditable =
  "border-border bg-background hover:border-muted-foreground/40 hover:shadow-sm transition cursor-pointer";
const chipReadonly = "border-border bg-muted/40 text-foreground cursor-default";

function AssigneeChip({
  assignee,
  onAssigneeChange,
  people,
}: {
  assignee: string | null;
  onAssigneeChange: (id: string | null) => void;
  people: Person[];
}) {
  const [open, setOpen] = useState(false);
  const person = people.find((p) => p.id === assignee);
  const name = personName(person);
  const pick = (id: string | null) => {
    onAssigneeChange(id);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-haspopup="dialog"
          className={cn(chipBase, chipEditable)}
        >
          {person ? (
            <InitialsAvatar name={name} size={18} />
          ) : (
            <User className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <span className="text-muted-foreground">Assignee</span>
          <span className={cn("font-semibold", !person && "text-muted-foreground")}>
            {person ? name : "Unassigned"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          <CommandInput placeholder="Assign to…" autoFocus />
          <CommandList>
            <CommandEmpty>No one found</CommandEmpty>
            <CommandGroup>
              <CommandItem value="Unassigned" onSelect={() => pick(null)} className="gap-2.5">
                <span className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-full border border-dashed border-muted-foreground/50 text-muted-foreground">
                  <User className="h-3.5 w-3.5" />
                </span>
                <span className="flex-1">Unassigned</span>
                {assignee == null && <Check className="h-4 w-4" />}
              </CommandItem>
              {people.map((p) => {
                const n = personName(p);
                return (
                  <CommandItem key={p.id} value={n} onSelect={() => pick(p.id)} className="gap-2.5">
                    <InitialsAvatar name={n} size={26} />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{n}</span>
                      {p.role && (
                        <span className="block truncate text-[11.5px] text-muted-foreground">
                          {p.role}
                        </span>
                      )}
                    </span>
                    {assignee === p.id && <Check className="h-4 w-4" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function DueChip({
  due,
  onDueChange,
}: {
  due: string | Date | null;
  onDueChange: (d: Date | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = due ? parseDateLocal(due) ?? undefined : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button aria-haspopup="dialog" className={cn(chipBase, chipEditable)}>
          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Due</span>
          <span className={cn("font-semibold", !selected && "text-muted-foreground")}>
            {selected ? format(selected, "MMM d, yyyy") : "Set date"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            onDueChange(d ?? null);
            setOpen(false);
          }}
          initialFocus
        />
        <div className="flex items-center justify-between border-t px-3 py-2">
          <button
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              onDueChange(new Date());
              setOpen(false);
            }}
          >
            Today
          </button>
          <button
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TaskChipRow({
  assignee,
  onAssigneeChange,
  due,
  onDueChange,
  clientName,
  people,
}: {
  assignee: string | null;
  onAssigneeChange: (id: string | null) => void;
  due: string | Date | null;
  onDueChange: (d: Date | null) => void;
  clientName?: string | null;
  people: Person[];
}) {
  return (
    <div className="flex flex-wrap gap-2.5">
      <AssigneeChip assignee={assignee} onAssigneeChange={onAssigneeChange} people={people} />
      {clientName && (
        <span className={cn(chipBase, chipReadonly)}>
          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Client</span>
          <span className="font-semibold">{clientName}</span>
        </span>
      )}
      <DueChip due={due} onDueChange={onDueChange} />
    </div>
  );
}
